import { SkeletonBodyText, SkeletonDisplayText } from "@shopify/polaris";

import { useAdminLoading } from "../utils/adminLoading";

/**
 * The loading picture for /integrations, defined ONCE and rendered at BOTH of that
 * route's gates:
 *   1. the Suspense fallback in components/App.jsx:608 (route chunk downloading),
 *   2. the `if (fetchingIntegration && !hasInitiallyLoaded)` branch in
 *      routes/integrations.jsx:300-302 (the emailProviderConfiguration query in
 *      flight).
 *
 * ⚠️ Both gates must keep rendering the SAME element. A route that uses this for
 * its `fetching` branch but leaves `RouteLoader` as its Suspense fallback (or vice
 * versa) reintroduces exactly the two-different-spinners defect described in
 * PageSkeleton.jsx. /integrations is the clearest case in the app that the wait is
 * STRUCTURAL rather than heavy: the chunk is 3.7 kB gz and it runs exactly one
 * query — it felt slow only because those two things happen strictly in series
 * with a different picture over each.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT `<PageSkeleton sections={[5]}/>` ANY MORE
 * ─────────────────────────────────────────────────────────────────────────────
 * A five-line grey blob is not what lands. What lands is a short paragraph and
 * then a FORM FIELD — label, select control, helper text — three elements with
 * three different heights and a hard visual edge around the control. Drawing the
 * field as a field is the difference between the handoff being invisible and the
 * section resettling. The one section title is a static string, so it is drawn as
 * a real heading rather than a skeleton bar, for the same reason PageSkeleton
 * insists the page `heading` matches: it is the anchor that does not move.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THE REAL PAGE IS (routes/integrations.jsx)
 * ─────────────────────────────────────────────────────────────────────────────
 *   :335  <s-page heading="Email Marketing Integration">            ← mirrored, byte-identical
 *   :337  <s-stack direction="block" gap="large">                   ← mirrored
 *   :350  <s-section heading="Manage Email Provider Integration">   ← mirrored
 *           :352  two-line explanatory paragraph
 *           :355  <s-select label="Select Your Email Provider" details="…">
 *                 → a label line, the control, and a line of helper text
 *
 * ⚠️ `heading` must stay byte-identical to routes/integrations.jsx:335's own
 * `<s-page heading="Email Marketing Integration">`, and the section heading to
 * :350's. Nothing enforces this — keep them in step by hand.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DELIBERATELY NOT DRAWN — all of it is CONDITIONAL
 * ─────────────────────────────────────────────────────────────────────────────
 * Reserving space for something most merchants never see creates a jump FOR THEM,
 * which is the opposite of the job. Omitted:
 * • The `Configure {provider}` section (:369). It renders only once `provider` is
 *   non-empty, and `provider` starts empty on every arrival — even for a shop that
 *   already has an integration, which is exactly why :338's "…is connected" banner
 *   is gated on `integration && !provider`. So this section is never present at
 *   the moment the skeleton hands over.
 * • The upgrade-gated `isFreePlan` page (:313-332) and its warning banner (:318).
 *   A different `<s-page>` entirely, for a subset of shops.
 * • The "{provider} is connected" banner (:338) and the API-key-scopes banner
 *   (:163). Both conditional, and both inside branches above.
 * • The `slot="breadcrumb-actions"` "Home" link (:336) — it renders in the admin's
 *   own fixed-height title bar, outside the body flow, so omitting it shifts
 *   nothing, and a skeleton must not paint a link that goes nowhere.
 *
 * NOTE ON PRIMITIVES: SkeletonDisplayText / SkeletonBodyText are Polaris REACT
 * holdouts — there is no `s-*` skeleton web component. See PageSkeleton.jsx for
 * why they survive vite-plugins/ns-polaris-css-purge.mjs. Everything structural is
 * a Polaris web component.
 */
export default function IntegrationsSkeleton() {
  // This component no longer renders <PageSkeleton>, which is where the call used
  // to live — so it must make the claim itself or Shopify's header progress bar
  // silently stops working for this route. Zero-arg and unconditional by design;
  // see utils/adminLoading.js.
  useAdminLoading();

  return (
    <s-page heading="Email Marketing Integration">
      <s-stack direction="block" gap="large">
        {/* :350 — the only section present on arrival. */}
        <s-section heading="Manage Email Provider Integration">
          <s-stack direction="block" gap="base">
            {/* :352 — "Automatically sync emails captured by Nudgesmart…" */}
            <SkeletonBodyText lines={2} />

            {/* :355 — the provider <s-select>: label, control, helper text. */}
            <s-stack direction="block" gap="small-200">
              <SkeletonDisplayText size="small" maxWidth="26ch" />
              <s-box
                blockSize="32px"
                borderWidth="base"
                borderColor="subdued"
                borderRadius="base"
                background="subdued"
              />
              <SkeletonBodyText lines={1} />
            </s-stack>
          </s-stack>
        </s-section>
      </s-stack>
    </s-page>
  );
}
